import React, {useEffect, useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {useTenant} from '../App';
import {clearPortalToken, getPortalToken, portalApi, publicApi, setPortalToken} from '../api';
import Spinner from '../components/Spinner';
import Footer from '../components/Footer';
import usePageTitle from '../hooks/usePageTitle';

const STAGE_ORDER = ['applied', 'screening', 'interview', 'offer', 'hired'];

export default function Portal() {
  const tenant = useTenant();
  const [searchParams] = useSearchParams();
  const [session, setSession] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [tab, setTab] = useState('status');
  const [status, setStatus] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [offer, setOffer] = useState(null);
  const [profileForm, setProfileForm] = useState(null);
  const [profileSaved, setProfileSaved] = useState(false);
  const [uploading, setUploading] = useState(false);

  usePageTitle(session ? 'My Application' : 'Portal');

  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (urlToken) setPortalToken(urlToken);
    const token = urlToken || getPortalToken();
    if (!token) { setInvalid(true); return; }
    publicApi(`/ats/public/portal/verify?token=${token}`).then(r => {
      if (r.ok) return r.json();
      clearPortalToken(); setInvalid(true); return null;
    }).then(s => { if (s) setSession(s); });
  }, [searchParams]);

  useEffect(() => { if (session) loadAll(); }, [session]);

  const loadAll = () => {
    portalApi('/ats/public/portal/status').then(r => r.ok ? r.json() : null).then(setStatus);
    portalApi('/ats/public/portal/documents').then(r => r.ok ? r.json() : []).then(setDocuments);
    portalApi('/ats/public/portal/interviews').then(r => r.ok ? r.json() : []).then(setInterviews);
    portalApi('/ats/public/portal/offer').then(r => r.ok ? r.json() : null).then(setOffer);
  };

  const handleLogout = () => { clearPortalToken(); setSession(null); setInvalid(true); };

  if (invalid) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="text-center max-w-md fade-in">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Session Expired</h1>
        <p className="text-gray-500 mb-6">Your link has expired or is invalid.</p>
        <Link to="/check-status" className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-2.5 rounded-lg hover:bg-teal-700 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2">
          Request a new link
        </Link>
      </div>
    </div>
  );

  if (!session || !status) return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Spinner className="flex-1 py-20" />
      <Footer />
    </div>
  );

  const stageIdx = STAGE_ORDER.indexOf(status.stage);
  const input = "w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none text-sm transition-colors";

  const tabs = [
    ['status', 'Profile'],
    ['documents', `Documents${status.attachmentCount ? ` (${status.attachmentCount})` : ''}`],
    ['interviews', `Interviews${status.interviewCount ? ` (${status.interviewCount})` : ''}`],
    ['offer', 'Offer Letter'],
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-gray-900 truncate">{tenant.name}</h1>
            <p className="text-sm text-gray-500">Welcome, {session.firstName}</p>
          </div>
          <nav className="flex items-center gap-3 sm:gap-4 flex-shrink-0" aria-label="Portal navigation">
            <Link to="/" className="text-sm text-teal-600 hover:text-teal-800 focus:outline-none focus:underline">Jobs</Link>
            <button onClick={handleLogout} className="text-sm text-gray-500 hover:text-red-600 focus:outline-none focus:underline">Sign Out</button>
          </nav>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex-1 w-full">
        {/* Status tracker */}
        <div className="bg-white rounded-lg shadow p-5 sm:p-6 mb-6 fade-in">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-5">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">{status.jobTitle || 'Your Application'}</h2>
              <p className="text-sm text-gray-500">Applied {status.appliedAt ? new Date(status.appliedAt).toLocaleDateString() : ''}</p>
            </div>
            {status.stage === 'rejected' && <span className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-red-50 text-red-700 self-start">Not Moving Forward</span>}
            {status.stage === 'hired' && <span className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-green-50 text-green-700 self-start">Hired! 🎉</span>}
          </div>
          {status.stage !== 'rejected' && (
            <>
              <div className="flex items-center gap-1 sm:gap-2" role="progressbar" aria-valuenow={stageIdx + 1} aria-valuemin={1} aria-valuemax={STAGE_ORDER.length}>
                {STAGE_ORDER.map((stage, i) => (
                  <React.Fragment key={stage}>
                    <div className={`flex items-center justify-center w-7 h-7 sm:w-9 sm:h-9 rounded-full text-xs font-bold transition-colors ${i <= stageIdx ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                      {i <= stageIdx ? '✓' : i + 1}
                    </div>
                    {i < STAGE_ORDER.length - 1 && <div className={`flex-1 h-1 rounded transition-colors ${i < stageIdx ? 'bg-teal-600' : 'bg-gray-200'}`} />}
                  </React.Fragment>
                ))}
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-500">
                {STAGE_ORDER.map(s => <span key={s} className="capitalize hidden sm:block">{s}</span>)}
              </div>
            </>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow fade-in">
          <div className="border-b border-gray-200 overflow-x-auto">
            <nav className="flex gap-4 sm:gap-6 px-4 sm:px-6 min-w-max" role="tablist" aria-label="Application sections">
              {tabs.map(([key, label]) => (
                <button key={key} onClick={() => setTab(key)} role="tab" aria-selected={tab === key}
                  className={`py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === key ? 'border-teal-600 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                  {label}
                </button>
              ))}
            </nav>
          </div>
          <div className="p-5 sm:p-6" role="tabpanel">
            {tab === 'status' && (
              <div className="fade-in">
                {!profileForm ? (
                  <div className="space-y-4">
                    {profileSaved && <div className="p-3 bg-green-50 text-green-700 text-sm rounded-lg fade-in" role="status">✓ Profile updated successfully.</div>}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div><p className="text-gray-500 mb-0.5">Name</p><p className="font-medium text-gray-900">{status.firstName} {status.lastName}</p></div>
                      <div><p className="text-gray-500 mb-0.5">Email</p><p className="font-medium text-gray-900">{status.email || '—'}</p></div>
                      <div><p className="text-gray-500 mb-0.5">Phone</p><p className="font-medium text-gray-900">{status.phone || '—'}</p></div>
                      <div><p className="text-gray-500 mb-0.5">Position</p><p className="font-medium text-gray-900">{status.jobTitle || '—'}</p></div>
                    </div>
                    <button onClick={() => { setProfileForm({email: status.email || '', phone: status.phone || ''}); setProfileSaved(false); }}
                      className="text-sm text-teal-600 hover:text-teal-800 font-medium focus:outline-none focus:underline">Edit Contact Info</button>
                  </div>
                ) : (
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    await portalApi('/ats/public/portal/profile', {method: 'PUT', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(profileForm)});
                    setProfileForm(null); setProfileSaved(true); loadAll();
                  }} className="space-y-4 max-w-md fade-in">
                    <div>
                      <label htmlFor="profile-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input id="profile-email" type="email" value={profileForm.email} onChange={e => setProfileForm({...profileForm, email: e.target.value})} autoComplete="email" className={input} />
                    </div>
                    <div>
                      <label htmlFor="profile-phone" className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input id="profile-phone" value={profileForm.phone} onChange={e => setProfileForm({...profileForm, phone: e.target.value})} autoComplete="tel" className={input} />
                    </div>
                    <div className="flex gap-2">
                      <button type="submit" className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2">Save</button>
                      <button type="button" onClick={() => setProfileForm(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 text-sm hover:bg-gray-50 transition-colors">Cancel</button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {tab === 'documents' && (
              <div className="fade-in">
                <form onSubmit={async (e) => {
                  e.preventDefault(); setUploading(true);
                  const fd = new FormData(e.target);
                  const token = getPortalToken();
                  await publicApi(`/ats/public/portal/documents?token=${token}`, {method: 'POST', body: fd});
                  e.target.reset(); setUploading(false); loadAll();
                }} className="flex flex-col sm:flex-row flex-wrap gap-3 mb-6 items-start sm:items-end p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label htmlFor="doc-file" className="block text-xs font-medium text-gray-700 mb-1">File <span className="text-red-500">*</span></label>
                    <input id="doc-file" type="file" name="file" required className="text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100 file:cursor-pointer" />
                  </div>
                  <div>
                    <label htmlFor="doc-label" className="block text-xs font-medium text-gray-700 mb-1">Label</label>
                    <input id="doc-label" name="label" placeholder="e.g. Cover Letter" className={`w-full sm:w-48 ${input}`} />
                  </div>
                  <button type="submit" disabled={uploading} className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 text-sm font-medium disabled:opacity-50 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2">
                    {uploading ? 'Uploading...' : 'Upload'}
                  </button>
                </form>
                {documents.length > 0 ? (
                  <div className="space-y-2">
                    {documents.map(d => (
                      <div key={d.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{d.fileName}</p>
                            <p className="text-xs text-gray-500">{d.label ? `${d.label} · ` : ''}{d.createdAt ? new Date(d.createdAt).toLocaleDateString() : ''}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <div className="text-3xl mb-3">📄</div>
                    <p className="text-gray-500 text-sm">No documents uploaded yet.</p>
                    <p className="text-gray-400 text-xs mt-1">Upload a cover letter, portfolio, or certifications above.</p>
                  </div>
                )}
              </div>
            )}

            {tab === 'interviews' && (
              <div className="fade-in">
                {interviews.length > 0 ? (
                  <div className="space-y-3">
                    {interviews.map(iv => (
                      <div key={iv.id} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{iv.scheduledAt ? new Date(iv.scheduledAt).toLocaleString(undefined, {weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'}) : 'TBD'}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {[iv.interviewer ? `With ${iv.interviewer}` : null, iv.type, iv.durationMinutes ? `${iv.durationMinutes}min` : null, iv.location].filter(Boolean).join(' · ')}
                            </p>
                          </div>
                          <span className={`inline-flex px-2 py-1 rounded text-xs font-medium self-start ${iv.status === 'completed' ? 'bg-green-50 text-green-700' : iv.status === 'cancelled' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>{iv.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <div className="text-3xl mb-3">📅</div>
                    <p className="text-gray-500 text-sm">No interviews scheduled yet.</p>
                    <p className="text-gray-400 text-xs mt-1">You'll see your interview details here when they're scheduled.</p>
                  </div>
                )}
              </div>
            )}

            {tab === 'offer' && (
              <div className="fade-in">
                {offer ? (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="p-5 sm:p-6">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{offer.jobTitle}</h3>
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium mt-1 ${offer.status === 'accepted' ? 'bg-green-50 text-green-700' : offer.status === 'declined' ? 'bg-red-50 text-red-700' : offer.status === 'sent' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{offer.status}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-4">
                        {offer.salary != null && <div><p className="text-gray-500">Salary</p><p className="font-medium text-gray-900 text-lg">${Number(offer.salary).toLocaleString()}</p></div>}
                        {offer.startDate && <div><p className="text-gray-500">Start Date</p><p className="font-medium text-gray-900">{new Date(offer.startDate + 'T00:00:00').toLocaleDateString(undefined, {weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'})}</p></div>}
                      </div>
                      {offer.content && <div className="text-sm text-gray-700 whitespace-pre-wrap border-t pt-4">{offer.content}</div>}
                    </div>
                    {(offer.status === 'sent' || offer.status === 'draft') && (
                      <div className="flex flex-col sm:flex-row gap-3 p-5 sm:p-6 bg-gray-50 border-t">
                        <button onClick={async () => {
                          await portalApi('/ats/public/portal/offer/respond', {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({response: 'accepted'})});
                          loadAll();
                        }} className="bg-green-600 text-white px-6 py-2.5 rounded-lg hover:bg-green-700 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2">
                          Accept Offer
                        </button>
                        <button onClick={async () => {
                          await portalApi('/ats/public/portal/offer/respond', {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({response: 'declined'})});
                          loadAll();
                        }} className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300">
                          Decline
                        </button>
                      </div>
                    )}
                    {offer.status === 'accepted' && (
                      <div className="p-5 sm:p-6 bg-green-50 border-t text-center">
                        <p className="text-green-700 font-medium">🎉 You accepted this offer! Congratulations!</p>
                      </div>
                    )}
                    {offer.status === 'declined' && (
                      <div className="p-5 sm:p-6 bg-gray-50 border-t text-center">
                        <p className="text-gray-500">You declined this offer.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <div className="text-3xl mb-3">📝</div>
                    <p className="text-gray-500 text-sm">No offer letter yet.</p>
                    <p className="text-gray-400 text-xs mt-1">Your offer letter will appear here when it's ready.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
